import React from 'react';
import { useFormikContext } from 'formik';
import { AcceptRequestFlowValues } from '../../../types/appointments';
import {
  depositPaidOptions,
  requestKindOptions,
} from '../../../utils/form/acceptRequestFlow';
import {
  allowOverlappingOptions,
  consultationLocationOptions,
  DEFAULT_CONSULT_LENGTH,
  depositPaymentMethodOptions,
  priceOptions,
} from '../../../utils/form/appointmentForm';
import { getLengthOptions } from '../../../helpers/appointments';
import {
  applyArtistHourlyRateDefault,
  getInitialArtistOptions,
  setVirtualFieldsFromEmployee,
} from '../../../helpers/employees';
import useBusinessEmployee from '../../../hooks/employees/useBusinessEmployee';
import useBusinessOrganization from '../../../hooks/businesses/useBusinessOrganization';
import { getEmployeeHourlyRate } from '../../../utils/form/acceptRequestPrefill';
import { InputGroupType, InputRowType, LoadedUser } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';
import useConfirm from '../../../hooks/useConfirm';
import useOnschedResource from '../../../hooks/employees/useOnschedResource';
import useEmployeeDurationValidation from '../../../hooks/employees/useEmployeeDurationValidation';
import { EmployeeAvailabilityError } from '../../../types/employees';
import { useArtistDefaultReplySync } from './useArtistDefaultReplySync';
import InputGroup from '../InputGroup';
import CashDiscountFormRow, {
  toCashDiscountPrice,
} from '../../payments/CashDiscountFormRow';

interface Props {
  hasFlashDesign: boolean;
  hasService: boolean;
  registerDurationValidator: (validator: () => Promise<boolean>) => void;
}

function AcceptRequestFields({
  hasFlashDesign,
  hasService,
  registerDurationValidator,
}: Props) {
  const { user: loadedUser } = useUser<LoadedUser>();
  const confirm = useConfirm();
  const { values, setFieldValue, touched, initialValues } =
    useFormikContext<AcceptRequestFlowValues>();

  const isConsultation = values.requestKind === 'CONSULTATION';

  const {
    hasMultipleBusinesses,
    businessOptions,
    getArtistOptionsForBusiness,
    getBusinessById,
  } = useBusinessOrganization();
  // Only requests belonging to the user's own shop can be moved — not trips
  // (no business) and not requests surfaced from another org business
  const canChangeLocation =
    hasMultipleBusinesses &&
    !!initialValues.businessId &&
    initialValues.businessId === loadedUser?.business.id;
  const selectedBusinessId = values.businessId || loadedUser?.business.id;

  useArtistDefaultReplySync({
    loadedUser,
    business: getBusinessById(selectedBusinessId),
  });

  const { data, isFetching } = useOnschedResource(values.employeeId);
  const { validate } = useEmployeeDurationValidation({
    availability: data?.availability,
    isLoading: isFetching,
    employeeId: values.employeeId,
  });

  const appointmentLengthOptions = React.useMemo(
    () =>
      getLengthOptions({
        user: loadedUser,
        appointmentType: values.requestKind,
        serviceId: values.serviceId ? Number(values.serviceId) : undefined,
        currentLength: values.appointmentLengthInMinutes
          ? Number(values.appointmentLengthInMinutes)
          : undefined,
        businessId: selectedBusinessId,
      }) || [],
    [
      loadedUser,
      selectedBusinessId,
      values.requestKind,
      values.serviceId,
      values.appointmentLengthInMinutes,
    ],
  );

  // True = the duration is fine to keep (possibly after the user confirmed a
  // warning); false = blocked. Never fails silently — an unavailable
  // availability check surfaces as its own confirmation.
  const checkDuration = React.useCallback(
    async (duration: string): Promise<boolean> => {
      let error: ReturnType<typeof validate>;
      try {
        error = validate(Number(duration));
      } catch {
        return confirm({
          dismissible: true,
          title: "Couldn't check availability",
          message:
            "We couldn't verify this artist's availability for the selected appointment length. Continue anyway?",
          positiveText: 'Continue',
          negativeText: 'Cancel',
        });
      }
      if (!error) return true;
      const durationError = error;

      const isWarning = durationError.type === 'warning';
      const confirmed = await confirm({
        dismissible: isWarning,
        title: durationError.errorTitle,
        message: durationError.errorMessage,
        positiveText: isWarning ? 'Continue' : 'Ok',
        negativeText: isWarning ? 'Cancel' : undefined,
      });
      if (durationError.code === EmployeeAvailabilityError.InvalidDuration) {
        const { maximumDuration } = durationError;
        const isMaxInList = appointmentLengthOptions.some(
          option => String(option.value) === String(maximumDuration),
        );
        if (isMaxInList) {
          setFieldValue(
            'appointmentLengthInMinutes',
            String(maximumDuration || ''),
          );
        }
        return false;
      }
      return isWarning && confirmed;
    },
    [appointmentLengthOptions, confirm, setFieldValue, validate],
  );

  React.useEffect(() => {
    registerDurationValidator(() =>
      checkDuration(String(values.appointmentLengthInMinutes)),
    );
  }, [
    checkDuration,
    registerDurationValidator,
    values.appointmentLengthInMinutes,
  ]);

  const touchedRef = React.useRef(touched);
  React.useEffect(() => {
    touchedRef.current = touched;
  }, [touched]);

  // Artist change (after mount): reset the length and re-apply the artist's
  // hourly-rate default, but never overwrite fields the user already touched
  const isFirstArtistRun = React.useRef(true);
  React.useEffect(() => {
    if (isFirstArtistRun.current) {
      isFirstArtistRun.current = false;
      return;
    }
    if (!values.employeeId || hasFlashDesign || hasService) return;

    setFieldValue('appointmentLengthInMinutes', '');

    applyArtistHourlyRateDefault(
      getEmployeeHourlyRate(loadedUser, values.employeeId) ??
        getBusinessById(selectedBusinessId)?.employeesToBusinesses.find(
          etb => etb.employeeId === values.employeeId,
        )?.defaultHourlyRate,
      touchedRef.current,
      setFieldValue,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.employeeId, hasFlashDesign, hasService, selectedBusinessId]);

  // Location change: keep the artist only when the destination shop also has
  // them, since availability tracks the employee, not the business
  const previousBusinessIdRef = React.useRef(values.businessId);
  React.useEffect(() => {
    const previousBusinessId = previousBusinessIdRef.current;
    previousBusinessIdRef.current = values.businessId;

    if (
      !canChangeLocation ||
      !values.businessId ||
      !previousBusinessId ||
      values.businessId === previousBusinessId
    ) {
      return;
    }

    const newBusinessHasArtist =
      !!values.employeeId &&
      !!getBusinessById(values.businessId)?.employeesToBusinesses.some(
        etb => etb.employeeId === values.employeeId,
      );

    if (!newBusinessHasArtist) {
      setFieldValue('employeeId', '');
    }
    setFieldValue('appointmentLengthInMinutes', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.businessId, canChangeLocation]);

  const { employee, employeeHasVirtualUrl } = useBusinessEmployee(
    values.employeeId,
    selectedBusinessId,
  );

  // Type toggle: consultations get the 15-minute default and the artist's
  // virtual preference; switching back restores the prefill
  const onRequestKindChange = React.useCallback(
    (kind: string) => {
      const isKindConsultation = kind === 'CONSULTATION';
      if (!touchedRef.current.appointmentLengthInMinutes) {
        setFieldValue(
          'appointmentLengthInMinutes',
          isKindConsultation
            ? DEFAULT_CONSULT_LENGTH
            : initialValues.appointmentLengthInMinutes,
        );
      }
      setVirtualFieldsFromEmployee(
        setFieldValue,
        employee,
        isKindConsultation && !!employee?.consultationVirtualByDefault,
      );
    },
    [employee, initialValues.appointmentLengthInMinutes, setFieldValue],
  );

  // Virtual URL/provider follow the Location select
  const onIsVirtualChange = React.useCallback(
    (value: string) =>
      setVirtualFieldsFromEmployee(
        setFieldValue,
        employee,
        String(value) === 'true',
      ),
    [employee, setFieldValue],
  );

  const inputGroup: InputGroupType = React.useMemo(() => {
    // Self-only for EMPLOYEE, full bookable roster otherwise; org moves
    // list the selected shop's roster instead
    const orgArtistOptions =
      canChangeLocation && loadedUser?.role !== 'EMPLOYEE'
        ? getArtistOptionsForBusiness(selectedBusinessId)
        : [];
    const artistOptions = orgArtistOptions.length
      ? orgArtistOptions
      : getInitialArtistOptions(loadedUser);

    return {
      name: 'Appointment Details',
      inputRows: [
        [
          {
            name: 'requestKind',
            label: 'Booking Type *',
            type: 'select',
            options: requestKindOptions,
            placeholder: 'Appointment or consultation',
            onChange: onRequestKindChange,
            tooltip:
              'Book the appointment itself, or start with a consultation to talk through the work first. Consultations default to 15 minutes and skip price, deposit & waiver.',
          },
        ],
        canChangeLocation &&
          businessOptions.length > 0 && [
            {
              name: 'businessId',
              label: 'Location',
              type: 'select',
              options: businessOptions,
              placeholder: 'Select a location',
            },
          ],
        [
          {
            name: 'employeeId',
            label: 'Artist *',
            type: 'select',
            options: artistOptions,
            placeholder: 'Select an artist for this appointment',
          },
        ],
        [
          {
            name: 'appointmentLengthInMinutes',
            label: 'Apt. Length *',
            type: 'select',
            options: appointmentLengthOptions,
            placeholder: 'How long?',
            validateChange: checkDuration,
            disabled: !values.employeeId || isFetching,
          },
        ],
        !isConsultation && [
          {
            name: 'priceType',
            label: 'Price Type *',
            type: 'select',
            options: priceOptions,
            placeholder: 'Exact, Range, or Hourly',
          },
          {
            name: 'price',
            label: 'Set Price *',
            type: 'dollar',
            placeholder: 'Enter price',
            hidden: {
              start: false,
              field: 'priceType',
              changesWhen: 'FIXED',
            },
          },
          {
            name: 'hourlyPrice',
            label: 'Hourly Price *',
            type: 'dollar',
            placeholder: 'Enter price',
            hidden: {
              start: true,
              field: 'priceType',
              changesWhen: 'HOURLY',
            },
          },
        ],
        !isConsultation && [
          {
            name: 'minPrice',
            label: 'Min Price *',
            type: 'dollar',
            placeholder: 'Enter min',
            hidden: {
              start: true,
              field: 'priceType',
              changesWhen: 'RANGE',
            },
          },
          {
            name: 'maxPrice',
            label: 'Max Price *',
            type: 'dollar',
            placeholder: 'Enter max',
            hidden: {
              start: true,
              field: 'priceType',
              changesWhen: 'RANGE',
            },
          },
        ],
        !isConsultation && [
          {
            name: 'price.cashDiscount',
            label: '',
            type: 'node',
            node: (
              <CashDiscountFormRow
                price={toCashDiscountPrice({
                  priceType: values.priceType,
                  price: values.price,
                  hourlyPrice: values.hourlyPrice,
                  minPrice: values.minPrice,
                  maxPrice: values.maxPrice,
                })}
              />
            ),
          },
        ],
        !isConsultation && [
          {
            name: 'depositPrice',
            label: 'Deposit *',
            type: 'dollar',
            placeholder: 'Enter amount (0 for no deposit)',
          },
        ],
        !isConsultation &&
          (values.depositPrice ?? 0) > 0 && [
            {
              name: 'deposit.cashDiscount',
              label: '',
              type: 'node',
              node: (
                <CashDiscountFormRow
                  price={{ type: 'FIXED', amount: values.depositPrice }}
                />
              ),
            },
          ],
        !isConsultation &&
          (values.depositPrice ?? 0) > 0 && [
            {
              name: 'depositPaid',
              label: 'Deposit already paid?',
              type: 'select',
              options: depositPaidOptions,
              placeholder: 'Has the client already paid the deposit?',
            },
          ],
        !isConsultation &&
          (values.depositPrice ?? 0) > 0 &&
          !!values.depositPaid && [
            {
              name: 'depositPaymentMethod',
              label: 'Payment Method',
              type: 'select',
              options: depositPaymentMethodOptions,
              placeholder: 'How did they pay?',
            },
          ],
        isConsultation &&
          employeeHasVirtualUrl && [
            {
              name: 'isVirtual',
              label: 'Location',
              type: 'select',
              options: consultationLocationOptions,
              placeholder: 'Select location type',
              onChange: onIsVirtualChange,
              tooltip:
                "Location defaults to the selected artist's preference. You can override it here.",
            },
          ],
        isConsultation && [
          {
            name: 'allowOverlapping',
            label: 'Double Book',
            type: 'select',
            options: allowOverlappingOptions,
            placeholder: 'Select an option',
          },
        ],
      ].filter(Boolean) as InputRowType[],
    };
  }, [
    appointmentLengthOptions,
    businessOptions,
    canChangeLocation,
    getArtistOptionsForBusiness,
    onIsVirtualChange,
    onRequestKindChange,
    checkDuration,
    employeeHasVirtualUrl,
    isConsultation,
    isFetching,
    loadedUser,
    selectedBusinessId,
    values.depositPaid,
    values.depositPrice,
    values.employeeId,
    values.hourlyPrice,
    values.maxPrice,
    values.minPrice,
    values.price,
    values.priceType,
  ]);

  return <InputGroup name={inputGroup.name} rows={inputGroup.inputRows} />;
}

export default AcceptRequestFields;
